// components/ChatWidget.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Send, X, Minimize2, Maximize2, MessageCircle, Sparkles, RefreshCw } from 'lucide-react';
import { ChatMessage } from './ChatMessage';
import { ChatInput } from './ChatInput';
import { LeadForm } from './LeadForm';
import { useChatbot } from '../hooks/useChatbot';
import type { ChatMessage as ChatMessageType } from '../types/chat.types';

interface ChatWidgetProps {
  title?: string;
  welcomeMessage?: string;
  source?: string;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ 
  title = "PlexiSystem AI",
  welcomeMessage = "Cześć! 👋 Jestem asystentem PlexiSystem.",
  source = "app"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showTypingIndicator, setShowTypingIndicator] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const {
    messages,
    isLoading,
    sendMessage,
    startConversation,
    endConversation
  } = useChatbot({
    conversationSource: source,
    welcomeMessage
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Resetuj licznik nieprzeczytanych przy otwarciu
  useEffect(() => {
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  // Symuluj otrzymanie nowej wiadomości
  useEffect(() => {
    if (!isOpen && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage.role === 'assistant') {
        setUnreadCount(prev => prev + 1);
      }
    }
  }, [messages, isOpen]);

  const handleSendMessage = async (content: string) => {
    setShowTypingIndicator(true);
    await sendMessage(content);
    setShowTypingIndicator(false);
  };

  const handleLeadSubmit = async (leadData: any) => {
    setShowLeadForm(false);
    await startConversation(leadData);
    
    // Jeśli user podał wiadomość w formularzu, wyślij ją od razu
    if (leadData.message && leadData.message.trim()) {
      setTimeout(async () => {
        await sendMessage(leadData.message);
      }, 1000); // Krótkie opóźnienie dla płynności
    }
  };

  const handleClose = () => {
    if (messages.length > 0 && confirm('Czy na pewno chcesz zakończyć rozmowę?')) {
      endConversation();
      setIsOpen(false);
    } else {
      setIsOpen(false);
    }
  };

  const toggleWidget = () => {
    if (isMinimized) {
      setIsMinimized(false);
    }
  };

  // Komponent przycisku czatu - dopasowany do stylu PlexiSystem
  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-20 right-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full p-4 shadow-2xl hover:shadow-orange-500/25 hover:scale-110 transition-all duration-300 group"
      >
        <MessageCircle className="w-6 h-6 group-hover:rotate-12 transition-transform" />
        {unreadCount > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold animate-pulse">
            {unreadCount}
          </span>
        )}
        <div className="absolute -inset-1 bg-orange-500 rounded-full opacity-0 group-hover:opacity-20 blur-xl transition-opacity"></div>
      </button>
    );
  }

  return (
    <div className={`fixed bottom-20 right-6 bg-zinc-900 rounded-2xl shadow-2xl transition-all duration-300 border border-zinc-700 ${
      isMinimized ? 'h-16 w-80' : 'h-[500px] w-96 max-w-[calc(100vw-3rem)]'
    } z-[9999]`}>
      {/* Header - w stylu PlexiSystem */}
      <div 
        className="bg-gradient-to-r from-zinc-800 to-zinc-700 text-white p-4 rounded-t-2xl flex justify-between items-center cursor-pointer border-b border-zinc-600" 
        onClick={toggleWidget}
      >
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-zinc-800"></div>
          </div>
          <div>
            <h3 className="font-semibold text-orange-400">{title}</h3>
            <p className="text-xs text-gray-400">Powered by Claude AI</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              if (messages.length > 0 && confirm('Czy na pewno chcesz rozpocząć nową rozmowę?')) {
                endConversation();
                setShowLeadForm(true);
              }
            }}
            className="hover:bg-zinc-600 p-1.5 rounded-lg transition-colors"
            title="Nowa rozmowa"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsMinimized(!isMinimized);
            }}
            className="hover:bg-zinc-600 p-1.5 rounded-lg transition-colors"
          >
            {isMinimized ? <Maximize2 className="w-4 h-4" /> : <Minimize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
            className="hover:bg-zinc-600 p-1.5 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Messages Container - ciemny motyw */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[calc(100%-8rem)] bg-zinc-950">
            {messages.length === 0 && !showLeadForm && (
              <div className="text-center py-8">
                <div className="mb-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500/20 to-orange-600/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur">
                    <MessageCircle className="w-10 h-10 text-orange-500" />
                  </div>
                  <p className="text-gray-300 mb-2 font-medium">
                    {welcomeMessage}
                  </p>
                  <p className="text-gray-500 text-sm mb-6">
                    Mogę pomóc w doborze produktów z pleksi, wycenie lub odpowiedzieć na pytania techniczne.
                  </p>
                </div>
                <button
                  onClick={() => setShowLeadForm(true)}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg hover:shadow-lg hover:shadow-orange-500/25 transition-all font-medium"
                >
                  Rozpocznij rozmowę
                </button>
              </div>
            )}

            {showLeadForm && (
              <LeadForm onSubmit={handleLeadSubmit} />
            )}

            {messages.map((message) => (
              <ChatMessage key={message.id} message={message} />
            ))}
            
            {(isLoading || showTypingIndicator) && (
              <div className="flex items-center gap-2 p-3">
                <div className="w-8 h-8 bg-zinc-800 rounded-full flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-orange-500" />
                </div>
                <div className="bg-zinc-800 rounded-2xl px-4 py-2 flex gap-1">
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce delay-100"></div>
                  <div className="w-2 h-2 bg-orange-500 rounded-full animate-bounce delay-200"></div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input - ciemny motyw */}
          {!showLeadForm && messages.length > 0 && (
            <ChatInput 
              onSendMessage={handleSendMessage} 
              disabled={isLoading}
              placeholder="Napisz wiadomość..."
            />
          )}
        </>
      )}
    </div>
  );
};